import React, { useEffect, useMemo, useState } from "react";
import {
  Laptop,
  CircleCheckBig,
  Wrench,
  SquarePen,
  Plus,
  CircleOff,
} from "lucide-react";
import AsyncSelect from "react-select/async";
import { employeeService } from "../../../services/employeeService";
import { equipmentService } from "../../../services/equipmentService";

const buildEmptyForm = () => ({
  name: "",
  serial_number: "",
  assigned_to: null,
  assigned_to_name: "",
  status: "",
});

const EquipmentForm = ({
  title,
  submitLabel,
  formData,
  formError,
  isSubmitting,
  onChange,
  onEmployeeChange,
  onCancel,
  onSubmit,
  employeeDefaults,
  loadEmployeeOptions,
}) => (
  <div className="border border-canopy-border rounded-xl p-5 bg-gray-50">
    <h3 className="text-2xl font-bold text-canopy-text mb-4">{title}</h3>

    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-canopy-text mb-2">
            Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-canopy-text mb-2">
            Serial #
          </label>
          <input
            type="text"
            name="serial_number"
            value={formData.serial_number}
            onChange={onChange}
            className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-canopy-text mb-2">
            Assigned To
          </label>
          <AsyncSelect
            isClearable
            cacheOptions
            defaultOptions={employeeDefaults}
            loadOptions={loadEmployeeOptions}
            value={
              formData.assigned_to
                ? {
                    value: formData.assigned_to,
                    label: formData.assigned_to_name,
                  }
                : null
            }
            placeholder="Select a team member"
            onChange={onEmployeeChange}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-canopy-text mb-2">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={onChange}
            className="w-full border border-canopy-border rounded-lg px-3 py-2 text-sm"
            required
          >
            <option value="">Select status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Available">Available</option>
          </select>
        </div>
      </div>

      {formError && (
        <p className="text-sm text-red-600 font-semibold">{formError}</p>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-semibold rounded-lg border border-canopy-border hover:bg-white transition"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="canopy-btn-primary"
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  </div>
);

const debounce = (fn, delay = 300) => {
  let timeout;
  return (...args) =>
    new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = setTimeout(async () => resolve(await fn(...args)), delay);
    });
};

const EquipmentOverview = ({ userPermission }) => {
  const [equipment, setEquipment] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editingEquipmentId, setEditingEquipmentId] = useState(null);
  const [formData, setFormData] = useState(buildEmptyForm());
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeDefaults, setEmployeeDefaults] = useState([]);
  const [activeFilter, setActiveFilter] = useState({ key: null, value: null });

  useEffect(() => {
    (async () => {
      try {
        const [{ items }, employees] = await Promise.all([
          equipmentService.getEquipment(),
          employeeService.getManagers("", []),
        ]);
        setEquipment(items);
        setEmployeeDefaults(
          employees.map((r) => ({ value: r.id, label: r.name })),
        );
      } catch (err) {
        console.error("Failed to load equipment", err);
      }
    })();
  }, []);

  const loadEmployeeOptions = async (inputValue) => {
    const rows = await employeeService.getManagers(inputValue, []);
    return rows.map((r) => ({ value: r.id, label: r.name }));
  };

  const debouncedLoadEmployees = useMemo(
    () => debounce(loadEmployeeOptions),
    [],
  );

  const handleEmployeeChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      assigned_to: option ? option.value : null,
      assigned_to_name: option ? option.label : "",
    }));
  };

  const statusStyles = {
    Maintenance: "bg-yellow-100 text-yellow-700",
    Active: "bg-green-100 text-green-700",
  };

  const stats = useMemo(() => {
    const totalEquipment = equipment.length;
    const activeEquipment = equipment.filter(
      (e) => e.status === "Active",
    ).length;
    const inactiveEquipment = equipment.filter(
      (e) => e.status === "Inactive",
    ).length;
    const maintenanceEquipment = equipment.filter(
      (e) => e.status === "Maintenance",
    ).length;
    const availableEquipment = equipment.filter(
      (e) => e.status === "Available",
    ).length;

    return [
      {
        label: "Total Equipment",
        value: totalEquipment,
        helper: "All equipment",
        icon: Laptop,
        helperClass: "text-canopy-blue",
        iconClass: "text-canopy-blue",
        filterKey: null,
        filterValue: null,
      },
      {
        label: "Active",
        value: activeEquipment,
        helper: "Currently active",
        icon: CircleCheckBig,
        helperClass: "text-green-600",
        iconClass: "text-green-600",
        filterKey: "status",
        filterValue: "Active",
      },
      {
        label: "Inactive",
        value: inactiveEquipment,
        helper: "Currently inactive",
        icon: CircleOff,
        helperClass: "text-red-600",
        iconClass: "text-red-600",
        filterKey: "status",
        filterValue: "Inactive",
      },
      {
        label: "Maintenance",
        value: maintenanceEquipment,
        helper: "Under maintenance",
        icon: Wrench,
        helperClass: "text-yellow-600",
        iconClass: "text-yellow-600",
        filterKey: "status",
        filterValue: "Maintenance",
      },
      {
        label: "Available",
        value: availableEquipment,
        helper: "Available equipment",
        icon: Laptop,
        helperClass: "text-gray-600",
        iconClass: "text-gray-600",
        filterKey: "status",
        filterValue: "Available",
      },
    ];
  }, [equipment]);

  const filteredEquipment = useMemo(() => {
    if (!activeFilter.key) return equipment;
    return equipment.filter((e) => e[activeFilter.key] === activeFilter.value);
  }, [equipment, activeFilter]);

  const handleStatClick = (filterKey, filterValue) => {
    if (
      !filterKey ||
      (activeFilter.key === filterKey && activeFilter.value === filterValue)
    ) {
      setActiveFilter({ key: null, value: null });
    } else {
      setActiveFilter({ key: filterKey, value: filterValue });
    }
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingEquipmentId(null);
    setFormData(buildEmptyForm());
    setFormError("");
  };

  const openCreateForm = () => {
    setFormMode("create");
    setEditingEquipmentId(null);
    setFormData(buildEmptyForm());
    setFormError("");
  };

  const openEditForm = (item) => {
    setFormMode("edit");
    setEditingEquipmentId(item.id);
    setFormData({
      name: item.name,
      serial_number: item.serial_number,
      assigned_to: item.assigned_to ?? null,
      assigned_to_name: item.assigned_to_name ?? "",
      status: item.status,
    });
    setFormError("");
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setIsSubmitting(true);
    try {
      if (formMode === "edit" && editingEquipmentId) {
        const updated = await equipmentService.updateEquipment(
          editingEquipmentId,
          formData,
        );
        setEquipment((previous) =>
          previous.map((e) => (e.id === editingEquipmentId ? updated : e)),
        );
      } else {
        const created = await equipmentService.createEquipment(formData);
        setEquipment((previous) => [...previous, created]);
      }
      closeForm();
    } catch (err) {
      setFormError(err.message || "Failed to save equipment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {stats.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.filterKey === null
                ? activeFilter.key === null
                : activeFilter.key === item.filterKey &&
                  activeFilter.value === item.filterValue;
            return (
              <button
                key={item.label}
                type="button"
                onClick={() =>
                  handleStatClick(item.filterKey, item.filterValue)
                }
                className={`text-left border rounded-xl p-5 bg-white transition-colors ${
                  isActive
                    ? "border-canopy-blue ring-2 ring-canopy-blue/10"
                    : "border-canopy-border hover:border-canopy-blue/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-canopy-text">
                    {item.label}
                  </h3>
                  <Icon className={`h-4 w-4 ${item.iconClass}`} />
                </div>
                <p className="text-4xl font-bold text-canopy-text mt-3">
                  {item.value}
                </p>
                <p className={`text-sm mt-2 ${item.helperClass}`}>
                  {item.helper}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="bg-white border border-canopy-border rounded-xl p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <h2 className="text-4xl/[1.1] sm:text-4xl font-bold text-canopy-text">
            Inventory
          </h2>

          <button
            type="button"
            onClick={openCreateForm}
            className="canopy-btn-primary inline-flex items-center justify-center gap-2 w-full md:w-auto"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>

        {formMode === "create" && (
          <div className="mt-6">
            <EquipmentForm
              title="Create an Equipment"
              submitLabel="Create"
              formData={formData}
              formError={formError}
              isSubmitting={isSubmitting}
              onChange={handleFormChange}
              onEmployeeChange={handleEmployeeChange}
              onCancel={closeForm}
              onSubmit={handleFormSubmit}
              employeeDefaults={employeeDefaults}
              loadEmployeeOptions={debouncedLoadEmployees}
            />
          </div>
        )}

        <div className="mt-6 space-y-4">
          {filteredEquipment.map((item) => {
            const isEditingThisEquipment =
              formMode === "edit" && editingEquipmentId === item.id;

            if (isEditingThisEquipment) {
              return (
                <div
                  key={item.id}
                  className="border border-canopy-border rounded-xl p-5 bg-white"
                >
                  <EquipmentForm
                    title="Edit Equipment"
                    submitLabel="Save Changes"
                    formData={formData}
                    formError={formError}
                    isSubmitting={isSubmitting}
                    onChange={handleFormChange}
                    onEmployeeChange={handleEmployeeChange}
                    onCancel={closeForm}
                    onSubmit={handleFormSubmit}
                    employeeDefaults={employeeDefaults}
                    loadEmployeeOptions={debouncedLoadEmployees}
                  />
                </div>
              );
            }

            return (
              <div
                key={item.id}
                className="border border-canopy-border rounded-xl p-5 bg-white"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-orange-500 text-white flex items-center justify-center font-semibold">
                      <Laptop className="h-4 w-4" />
                    </div>

                    <div>
                      <p className="text-2xl font-bold text-canopy-text">
                        {item.name}
                      </p>
                      <p className="text-sm text-canopy-textMuted">
                        Serial #: {item.serial_number}
                      </p>
                      <p className="text-sm text-canopy-textMuted mt-1">
                        Assigned To: {item.assigned_to_name || "Unassigned"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                        statusStyles[item.status] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {item.status}
                    </span>

                    <button
                      type="button"
                      onClick={() => openEditForm(item)}
                      className="px-3 py-1 text-xs font-semibold rounded-lg border border-canopy-border hover:bg-gray-100 transition"
                    >
                      <SquarePen className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default EquipmentOverview;
